// DS8.4
import React, { useCallback, useContext, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import UserAvatar from '../../../../User/Avatar';
import UserAvatarPlaceholder from '../../../../User/AvatarPlaceholder';
import Button from '../../../../Button';
import { useCurrentUser } from '../../../../utils/hooks/useCurrentUser';
import { getVerificationEmailAddress } from '../../../../publicSession/selectors';
import LoggedInMenu from './_partials/LoggedInMenu';
import LoggedOutMenu from './_partials/LoggedOutMenu';

import SiteHeaderStyles from '../../../SiteHeaderDesktop/SiteHeaderDesktop.scss';
import s from './UserMenuDesktop.scss';

export interface Props {
  isOpen: boolean;
  className?: string;
  disableThemeSwitcher?: boolean;
  innerRef?: React.RefObject<HTMLElement>;
}

export interface State {
  touchIsOpen?: boolean;
}

const UserMenuDesktop: React.FC<Props> = ({
  isOpen,
  className,
  disableThemeSwitcher,
  innerRef,
}) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useContext(SessionContext);
  const verificationEmail = useSelector(getVerificationEmailAddress);
  const currentUser = useCurrentUser();

  const [touchIsOpen, setTouchIsOpen] = useState(false);

  const handleTouchEnd = useCallback(e => {
    e.preventDefault();
    setTouchIsOpen(true);
  }, []);

  const handleClickUserMenuOpen = useCallback(() => {
    setTouchIsOpen(!touchIsOpen);
  }, [touchIsOpen]);

  return (
    <div
      className={classnames(
        s['user-dropdown'],
        SiteHeaderStyles['icon-button-has-submenu'],
        (isOpen || touchIsOpen) && SiteHeaderStyles['active'],
        verificationEmail && SiteHeaderStyles['pulsing-animation']
      )}
      ref={innerRef as any}
    >
      <Button
        view="b3"
        className={s['user-menu-button']}
        aria-haspopup={true}
        aria-expanded={isOpen}
        aria-controls="site-header-user-menu"
        onClick={handleClickUserMenuOpen}
      >
        {t('siteHeader.userMenu.open')}
      </Button>
      {isLoggedIn && (
        <>
          <UserAvatar
            user={currentUser}
            size={30}
            className={s['avatar']}
            withLink={true}
            onTouchEnd={e => handleTouchEnd(e)}
            withTooltip={false}
            biData={{ sectionname: 'header' }}
          />
          <LoggedInMenu disableThemeSwitcher={disableThemeSwitcher} className={className} />
        </>
      )}
      {!isLoggedIn && (
        <>
          <div className={s['avatar']}>
            <UserAvatarPlaceholder width={30} height={30} alt="" />
          </div>
          <LoggedOutMenu disableThemeSwitcher={disableThemeSwitcher} className={className} />
        </>
      )}
    </div>
  );
};
UserMenuDesktop.displayName = 'UserMenuDesktop';

export default UserMenuDesktop;
